import { useMemo } from 'react';
import { DateLocalizer } from 'react-big-calendar';
// @ts-expect-error no types exported
import timeGridModule from 'react-big-calendar/lib/TimeGrid';
import { DateTime } from 'luxon';
import { WeekStartDay } from '../../../store';
import { scheduling } from '../../../store/cabinetApi/scheduling';

// React Big Calendar's internal TimeGrid module is CommonJS, so Vite/Rolldown
// can expose it as either the component itself or a default-wrapped component.
const TimeGrid = 'default' in timeGridModule ? timeGridModule.default : timeGridModule;

function CustomWorkWeek({
  date,
  localizer,
  max = localizer.endOf(new Date(), 'day'),
  min = localizer.startOf(new Date(), 'day'),
  scrollToTime,
  ...props
}: { date: Date; localizer: DateLocalizer; max: Date; min: Date; scrollToTime: boolean }) {
  // Get the work week start day preference
  const { data: schedulingPrefs } = scheduling.useAppSchedulingPreferencesListQuery();
  const workWeekStartDay = schedulingPrefs?.user_prefs?.work_week_start_day 
    ? WeekStartDay[schedulingPrefs.user_prefs.work_week_start_day] 
    : null;

  const currRange = useMemo(
    () => CustomWorkWeek.range(date, { localizer, workWeekStartDay }),
    [date, localizer, workWeekStartDay]
  );

  return (
    <TimeGrid
      date={date}
      eventOffset={15}
      localizer={localizer}
      max={max}
      min={min}
      range={currRange}
      scrollToTime={scrollToTime}
      enableAutoScroll={true}
      {...props}
    />
  );
}

CustomWorkWeek.range = (
  date: Date, 
  { localizer, workWeekStartDay }: { localizer: DateLocalizer; workWeekStartDay?: WeekStartDay | null }
) => {
  // @ts-expect-error this wants an argument but does not seem to have issues without it
  const firstOfWeek = localizer.startOfWeek();
  const start = localizer.startOf(date, 'week', firstOfWeek) as Date;
  const end = localizer.endOf(date, 'week', firstOfWeek) as Date;

  const weekRange = localizer.range(start, end) as Date[];
  
  // Adjust the range based on work week start day preference
  const regularWeekStartDay = Number(firstOfWeek) === 7 ? WeekStartDay.SUNDAY : WeekStartDay.MONDAY;
  if (workWeekStartDay && workWeekStartDay !== regularWeekStartDay) {
    // Work week start day differs from regular week start day
    const startDateTime = DateTime.fromJSDate(start);
    let workWeekStart: DateTime;
    
    if (workWeekStartDay === WeekStartDay.SUNDAY && Number(firstOfWeek) === 1) {
      // Work week starts Sunday, regular week starts Monday - need Sunday-Thursday
      workWeekStart = startDateTime.minus({ days: 1 }); // Go back one day to get Sunday
    } else if (workWeekStartDay === WeekStartDay.MONDAY && Number(firstOfWeek) === 7) {
      // Work week starts Monday, regular week starts Sunday - need Monday-Friday
      workWeekStart = startDateTime.plus({ days: 1 }); // Go forward one day to get Monday
    } else {
      // Fallback to default behavior
      return weekRange.slice(0, 5);
    }
    
    const workWeekRange: Date[] = [];
    for (let i = 0; i < 5; i++) {
      workWeekRange.push(workWeekStart.plus({ days: i }).toJSDate());
    }
    return workWeekRange;
  }
  
  // Default behavior: return first 5 days
  return weekRange.slice(0, 5);
};

CustomWorkWeek.navigate = (date: Date, action: unknown, { localizer}: { localizer: DateLocalizer }) => {
  return date;
};

CustomWorkWeek.title = (date: Date) => {
  return `Week of: ${date.toLocaleDateString()}`;
};

export default CustomWorkWeek;
